import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';
import { createRotatedLabelDualAxisConfig } from './chartConfigUtils';

interface RetentionData {
    readonly period: string;
    readonly newCustomers: number;
    readonly returningCustomers: number;
    readonly retentionRate: number;
    readonly churnRate: number;
    readonly avgContractsPerCustomer: number;
}

interface RetentionChartProps {
    readonly data: readonly RetentionData[];
    readonly loading?: boolean;
}

function createRetentionTooltipFormatter(
    data: readonly RetentionData[],
    t: (key: string, defaultValue: string) => string,
): Highcharts.TooltipFormatterCallbackFunction {
    return function () {
        const points = this.points;
        if (!points) return '';
        let tooltip = `<b>${this.x}</b><br/>`;
        points.forEach(point => {
            const suffix = point.series.name.includes('Rate') ? '%' : '';
            tooltip += `${point.series.name}: <b>${Highcharts.numberFormat(Number(point.y), point.series.name.includes('Rate') ? 1 : 0)}${suffix}</b><br/>`;
        });
        const dataPoint = data.find(d => d.period === String(this.x));
        if (dataPoint) {
            tooltip += `<br/>${t('statistics.advanced.avgContractsPerCustomer', 'Avg Contracts/Customer')}: <b>${dataPoint.avgContractsPerCustomer}</b>`;
        }
        return tooltip;
    };
}

function RetentionChart({ data, loading = false }: RetentionChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const categories = data.map(d => d.period);
    const base = createRotatedLabelDualAxisConfig(
        categories,
        t('statistics.advanced.period', 'Period'),
        t('statistics.advanced.customers', 'Customers'),
        t('statistics.advanced.retentionRate', 'Retention Rate (%)'),
    );

    // Override second yAxis to add max: 100
    const yAxisConfig = Array.isArray(base.yAxis) ? [...base.yAxis] : [];
    if (yAxisConfig[1]) {
        yAxisConfig[1] = { ...yAxisConfig[1], max: 100 };
    }

    const options: Highcharts.Options = {
        ...base,
        yAxis: yAxisConfig,
        tooltip: {
            shared: true,
            formatter: createRetentionTooltipFormatter(data, t),
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                borderWidth: 0,
            },
        },
        series: [
            {
                type: 'column',
                name: t('statistics.advanced.newCustomers', 'New Customers'),
                data: data.map(d => d.newCustomers),
                color: '#1890ff',
                stack: 'customers',
                yAxis: 0,
            },
            {
                type: 'column',
                name: t('statistics.advanced.returningCustomers', 'Returning Customers'),
                data: data.map(d => d.returningCustomers),
                color: '#52c41a',
                stack: 'customers',
                yAxis: 0,
            },
            {
                type: 'spline',
                name: t('statistics.advanced.retentionRate', 'Retention Rate'),
                data: data.map(d => d.retentionRate),
                color: '#722ed1',
                lineWidth: 3,
                yAxis: 1,
                marker: {
                    enabled: true,
                    radius: 5,
                },
            },
        ],
        credits: {
            enabled: false,
        },
        legend: {
            enabled: true,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default RetentionChart;
